import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useUIStore } from '../../stores';
import { API_URL } from '../../config';
import { Loader2 } from 'lucide-react';

const API = API_URL;

export default function OAuthCallback() {
  const navigate = useNavigate();
  const { addToast } = useUIStore();

  useEffect(() => {
    // Token arrives in URL fragment so it is never sent to any server in a Referer header
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const token = params.get('token');

    // Clear the fragment immediately so the token is not left in browser history
    window.history.replaceState(null, '', window.location.pathname);

    if (!token) {
      const errorCode = new URLSearchParams(window.location.search).get('error') || 'unknown';
      addToast({ type: 'error', title: 'Sign-in failed', message: `OAuth error: ${errorCode}` });
      navigate('/login', { replace: true });
      return;
    }

    // L-2: always hydrate user from /auth/me rather than trusting URL fragment data.
    // This ensures role, plan, and all fields come from the authoritative backend.
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error(`/auth/me returned ${res.status}`);
        }

        const user = await res.json();

        useAuthStore.setState({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        });

        addToast({
          type: 'success',
          title: 'Welcome!',
          message: `Signed in as ${user.email}`,
        });

        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('[OAuthCallback] Failed to hydrate user:', err);
        addToast({
          type: 'error',
          title: 'Sign-in failed',
          message: 'Could not verify your session. Please try again.',
        });
        navigate('/login', { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500 mx-auto mb-4" />
        <p className="text-gray-400">Signing you in...</p>
      </div>
    </div>
  );
}
